import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { extractPdfTextBrowser } from "@/lib/pdf-utils";
import { extractXlsxTextBrowser } from "@/lib/xlsx-utils";
import { compressImage } from "@/lib/image-utils";
import { PerformanceTracker } from "@/lib/performance-tracker";
import { processFileUploadUltraFast, cleanOldCache } from './smartfile-fast';

// Helper: Comparação profunda para objetos/arrays (resolve problema com XLSX/CSV metadata)
function deepEqual(a: any, b: any): boolean {
  // Valores idênticos ou ambos null/undefined
  if (a === b) return true;
  if (a == null && b == null) return true; // null == undefined = true
  if (a == null || b == null) return false;

  // Converter números para strings se necessário (PostgreSQL pode fazer isso)
  if (typeof a === 'number' && typeof b === 'string') return String(a) === b;
  if (typeof a === 'string' && typeof b === 'number') return a === String(b);

  // Tipos diferentes
  if (typeof a !== typeof b) return false;

  // Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => deepEqual(val, b[idx]));
  }

  // Objetos
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a).filter(k => a[k] !== undefined);
    const keysB = Object.keys(b).filter(k => b[k] !== undefined);

    // Ignorar campos undefined
    const allKeys = new Set([...keysA, ...keysB]);

    for (const key of allKeys) {
      if (!deepEqual(a[key], b[key])) return false;
    }

    return true;
  }

  return false;
}

// Helper: Comparação via JSON serialization (fallback para edge cases)
function jsonEqual(a: any, b: any): boolean {
  try {
    const sortObject = (obj: any): any => {
      if (Array.isArray(obj)) return obj.map(sortObject);
      if (obj && typeof obj === 'object') {
        return Object.keys(obj)
          .sort()
          .reduce((acc, key) => {
            acc[key] = sortObject(obj[key]);
            return acc;
          }, {} as any);
      }
      return obj;
    };

    return JSON.stringify(sortObject(a)) === JSON.stringify(sortObject(b));
  } catch {
    return false;
  }
}

// Helper: Atualizar extracted_data SEMPRE fazendo merge (NUNCA sobrescrever)
// Fase 3.1: Simplificado - removido loop de retry complexo
async function updateExtractedData(
  sessionId: string,
  newData: Record<string, any>
): Promise<boolean> {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}][updateExtractedData][${sessionId}] START - New data:`, newData);

  // Buscar dados atuais
  const { data: session, error: fetchError } = await supabase
    .from("smartfile_sessions")
    .select("extracted_data")
    .eq("id", sessionId)
    .single();

  if (fetchError) {
    console.error(`[${timestamp}][updateExtractedData][${sessionId}] ❌ Error fetching session:`, fetchError);
    return false;
  }

  const currentData = session?.extracted_data && typeof session.extracted_data === 'object'
    ? session.extracted_data as Record<string, any>
    : {};

  const mergedData = {
    ...currentData,
    ...newData
  };

  // Tentativa simples única
  const { error: updateError } = await supabase
    .from("smartfile_sessions")
    .update({ extracted_data: mergedData })
    .eq("id", sessionId);

  if (updateError) {
    console.error(`[${timestamp}][updateExtractedData][${sessionId}] ❌ Update failed:`, updateError);
    return false;
  }

  console.log(`[${timestamp}][updateExtractedData][${sessionId}] ✅ SUCCESS`);
  return true;
}

// Helper: Sanitizar nome de arquivo para Supabase Storage
function sanitizeFileName(fileName: string): string {
  // Separar nome e extensão
  const lastDotIndex = fileName.lastIndexOf(".");
  const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : "";

  // Normalizar caracteres acentuados e remover caracteres especiais
  const sanitized = name
    .normalize("NFD") // Decompor caracteres acentuados
    .replace(/[\u0300-\u036f]/g, "") // Remover diacríticos
    .replace(/[^a-zA-Z0-9-_]/g, "_") // Substituir caracteres especiais por underscore
    .replace(/_+/g, "_") // Evitar múltiplos underscores consecutivos
    .replace(/^_|_$/g, ""); // Remover underscores no início/fim

  return sanitized + extension.toLowerCase();
}

export type SmartfileSession = {
  id: string;
  file_name: string;
  file_type: string;
  file_path: string;
  extracted_text?: string;
  extraction_status?: string; // ✅ Status da extração (pending, completed, failed)
  content_type?: string;
  structured_records?: any;
  ai_summary?: string;
  ai_insights?: any;
  results_generated?: number;
  lab_results_ids?: string[];
  results_generation_attempted?: boolean; // ✅ Flag para prevenir loop
  health_record_ids?: string[]; // ✅ IDs dos registros de saúde criados
  status: string;
  step: number;
  professional_notes?: string;
  temp_patient_data?: any;
  patient_id?: string;
  metadata?: any;
};

// 1. Criar sessão inicial
export async function createSmartfileSession(
  patientId: string | null,
  tempPatientData?: { name: string; cpf: string }
): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Usuário não autenticado");

  const { data, error } = await supabase
    .from("smartfile_sessions")
    .insert({
      user_id: user.id,
      patient_id: patientId,
      temp_patient_data: tempPatientData,
      status: "uploading",
      step: 1,
      file_name: "",
      file_path: "",
      file_type: "",
    })
    .select()
    .single();

  if (error) throw error;
  return data.id;
}

// 2. Upload de arquivo para storage
// Fase 3.2: Adiciona compressão de imagens antes do upload
export async function uploadSmartfileFile(
  sessionId: string,
  file: File
): Promise<{ filePath: string; fileType: string }> {
  // Detectar tipo baseado em MIME type e extensão
  let fileType = file.type; // Ex: "audio/webm"

  // Se não houver MIME type, usar extensão
  if (!fileType) {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    fileType = ext;
  }

  // Fase 3.2: Comprimir imagens grandes (> 2MB)
  let fileToUpload = file;
  const isImage = fileType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp'].includes(
    file.name.split('.').pop()?.toLowerCase() || ''
  );

  if (isImage && file.size > 2 * 1024 * 1024) {
    try {
      fileToUpload = await compressImage(file, {
        maxWidth: 2000,
        maxHeight: 2000,
        quality: 0.85,
        maxSizeMB: 2,
      });
      console.log(`[uploadSmartfileFile] Imagem comprimida: ${file.size} → ${fileToUpload.size} bytes`);
    } catch (error) {
      console.warn('[uploadSmartfileFile] Erro ao comprimir imagem, usando original:', error);
    }
  }

  // Sanitizar nome do arquivo para evitar caracteres especiais
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Usuário não autenticado");
  const sanitizedName = sanitizeFileName(fileToUpload.name);
  const fileName = `${user.id}/${sessionId}/${Date.now()}_${sanitizedName}`;

  // Upload para bucket patient-docs (reutilizando bucket existente)
  const { data, error } = await supabase.storage
    .from("patient-docs")
    .upload(fileName, fileToUpload);

  if (error) throw error;

  // Atualizar sessão com informações do arquivo
  await supabase
    .from("smartfile_sessions")
    .update({
      file_name: file.name,
      file_path: fileName,
      file_type: fileType,
      file_size: fileToUpload.size,
      status: "uploaded",
    })
    .eq("id", sessionId);

  return { filePath: fileName, fileType };
}

// 3. Extrair texto (com fallback para extração local)
// Fase 1.1 e 1.2: Extração local de PDF/XLSX antes de chamar edge function
export async function extractSmartfileText(
  sessionId: string,
  filePath: string,
  fileType: string,
  file?: File
): Promise<string> {
  const tracker = new PerformanceTracker();
  tracker.start();
  tracker.mark('extraction_start');

  // Fase 1.1 e 1.2: Tentar extração local primeiro para PDF e XLSX
  if (file) {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const isPdf = ext === 'pdf' || fileType.includes('pdf');
    const isXlsx = ['xlsx', 'xls'].includes(ext) || fileType.includes('spreadsheet') ||
      fileType.includes('excel');

    if (isPdf) {
      try {
        tracker.mark('local_pdf_start');
        const text = await extractPdfTextBrowser(file);
        tracker.mark('local_pdf_end');
        const extractionTime = tracker.measure('local_pdf_start', 'local_pdf_end');

        console.log(`[extractSmartfileText] PDF extraído localmente em ${extractionTime}ms`);

        // Salvar texto e metadata no banco
        await supabase
          .from("smartfile_sessions")
          .update({
            extracted_text: text,
            extraction_status: "completed",
            status: "extracted",
            extraction_method: "client",
          })
          .eq("id", sessionId);

        return text;
      } catch (error) {
        console.warn('[extractSmartfileText] Erro na extração local de PDF, usando edge function:', error);
      }
    }

    if (isXlsx) {
      try {
        tracker.mark('local_xlsx_start');
        const text = await extractXlsxTextBrowser(file);
        tracker.mark('local_xlsx_end');
        const extractionTime = tracker.measure('local_xlsx_start', 'local_xlsx_end');

        console.log(`[extractSmartfileText] XLSX extraído localmente em ${extractionTime}ms`);

        // Salvar texto no banco
        await supabase
          .from("smartfile_sessions")
          .update({
            extracted_text: text,
            extraction_status: "completed",
            status: "extracted",
            extraction_method: "client",
          })
          .eq("id", sessionId);

        return text;
      } catch (error) {
        console.warn('[extractSmartfileText] Erro na extração local de XLSX, usando edge function:', error);
      }
    }
  }

  // Fallback: usar edge function para outros tipos
  tracker.mark('edge_function_start');
  const { data, error } = await supabase.functions.invoke(
    "smartfile-v15-extract-text",
    {
      body: { filePath, fileType, sessionId },
    }
  );

  tracker.mark('edge_function_end');
  
  if (data?.aiModel) {
    console.log(`[extractSmartfileText] 🤖 Extraction AI Model: ${data.aiModel.provider}/${data.aiModel.model_code} (${data.aiModel.name || 'N/A'})`);
  }
  const edgeTime = tracker.measure('edge_function_start', 'edge_function_end');

  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}][extractSmartfileText][${sessionId}] Edge function time: ${edgeTime}ms`);

  // Tratamento específico de erro
  if (error) {
    console.error(`[${timestamp}][extractSmartfileText][${sessionId}] ❌ Edge function error:`, error);

    // Mensagens específicas por tipo de erro
    let errorMessage = 'Erro ao extrair texto do arquivo';

    if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // Verificações específicas de erro
    if (error.message?.includes("API_KEY") || error.message?.includes("GOOGLE_GEMINI_API_KEY")) {
      throw new Error("GOOGLE_GEMINI_API_KEY não configurada. Configure nas variáveis de ambiente.");
    }
    if (error.message?.includes("RESOURCE_EXHAUSTED") || error.message?.includes("429")) {
      throw new Error("Limite de requisições da API Google Gemini atingido. Tente novamente em alguns minutos.");
    }

    // Erro específico para planilhas
    const isSpreadsheet = fileType.includes('spreadsheet') ||
      fileType.includes('xlsx') ||
      fileType.includes('xls') ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    if (isSpreadsheet) {
      throw new Error(
        `❌ Erro ao processar planilha: ${errorMessage}\n\n` +
        `Dicas:\n` +
        `• Verifique se o arquivo não está corrompido\n` +
        `• Tente abrir e salvar novamente no Excel/Sheets\n` +
        `• Reduza o tamanho removendo dados desnecessários\n` +
        `• Tamanho máximo: 10MB`
      );
    }

    throw new Error(errorMessage);
  }

  if (!data?.success) {
    throw new Error(data?.error || 'Falha na extração de texto');
  }

  if (!data?.text) {
    throw new Error('Texto não extraído. Arquivo pode estar vazio.');
  }

  console.log(`[${timestamp}][extractSmartfileText][${sessionId}] Text extracted, length: ${data.text.length}`);

  // Fase 1.4: Batch update - atualizar todos os campos de uma vez (inclui metadata se houver)
  const updateData: any = {
    extracted_text: data.text,
    extraction_status: "completed",
    status: "extracted",
    extraction_method: "server",
  };

  // Se houver metadata, incluir no update
  if (data.metadata) {
    // Buscar dados atuais para fazer merge
    const { data: session } = await supabase
      .from("smartfile_sessions")
      .select("extracted_data")
      .eq("id", sessionId)
      .single();

    const currentData = session?.extracted_data && typeof session.extracted_data === 'object'
      ? session.extracted_data as Record<string, any>
      : {};

    updateData.extracted_data = {
      ...currentData,
      ...data.metadata
    };
  }

  await supabase
    .from("smartfile_sessions")
    .update(updateData)
    .eq("id", sessionId);

  tracker.mark('extraction_end');
  const metrics = tracker.end({ extractionMethod: 'server' });
  await PerformanceTracker.saveMetrics(sessionId, metrics);

  console.log(`[${timestamp}][extractSmartfileText][${sessionId}] ✅ SUCCESS`);
  return data.text;
}

// 4. Classificar conteúdo (Edge Function)
export async function classifySmartfileContent(
  sessionId: string,
  extractedText: string,
  fileType: string
): Promise<string> {
  const { data, error } = await supabase.functions.invoke(
    "smartfile-v15-classify-content-fast",
    {
      body: { extractedText, fileType },
    }
  );

  if (error) {
    if (error.message?.includes("API_KEY") || error.message?.includes("GOOGLE_GEMINI_API_KEY")) {
      throw new Error("GOOGLE_GEMINI_API_KEY não configurada. Configure nas variáveis de ambiente.");
    }
    if (error.message?.includes("RESOURCE_EXHAUSTED") || error.message?.includes("429")) {
      throw new Error("Limite de requisições da API Google Gemini atingido. Tente novamente em alguns minutos.");
    }
    throw error;
  }

  if (!data.success) {
    throw new Error(data.error || "Falha na classificação de conteúdo");
  }

  // Use fallback if contentType not present
  const contentType = data.contentType || data.classification?.category || "other";

  if (!contentType) {
    console.error("Resposta inesperada:", data);
    throw new Error("Resposta da API sem contentType definido");
  }

  // Atualizar sessão
  await supabase
    .from("smartfile_sessions")
    .update({
      content_type: contentType,
      ai_classification: data.classification || data,
      status: "classified",
    })
    .eq("id", sessionId);

  return contentType;
}

// 5. Estruturar registros (Edge Function)
export async function structureSmartfileRecords(
  sessionId: string,
  extractedText: string,
  contentType: string
): Promise<any[]> {
  const { data, error } = await supabase.functions.invoke(
    "smartfile-v15-structure-records-fast",
    {
      body: { extractedText, contentType },
    }
  );

  if (error) {
    if (error.message?.includes("API_KEY") || error.message?.includes("GOOGLE_GEMINI_API_KEY")) {
      throw new Error("GOOGLE_GEMINI_API_KEY não configurada. Configure nas variáveis de ambiente.");
    }
    if (error.message?.includes("RESOURCE_EXHAUSTED") || error.message?.includes("429")) {
      throw new Error("Limite de requisições da API Google Gemini atingido. Tente novamente em alguns minutos.");
    }
    throw error;
  }
  if (!data.success || !data.records) {
    throw new Error("Falha na estruturação de registros");
  }

  // Função para mapear contentType para tipo de registro
  const mapContentTypeToRecordType = (contentType: string): string => {
    const typeMap: Record<string, string> = {
      'physiotherapy_evolution': 'physiotherapy_evolution',
      'anamnesis': 'anamnesis',
      'results': 'results',
      'vitals': 'vitals',
      'nutritional_assessment': 'nutritional_assessment',
      'meal_plan': 'meal_plan',
      'training_plan': 'training_plan',
      'body_composition': 'body_composition',
      'psychotherapy_session': 'psychotherapy_session',
      'chiropractic_adjustment': 'chiropractic_adjustment',
      'aesthetic_protocol': 'aesthetic_protocol',
      'anthropometry': 'anthropometry',
      'exam': 'exam',
      'report': 'report',
      'prescription': 'prescription',
    };
    return typeMap[contentType] || 'consulta';
  };

  // Função para gerar título baseado no contentType
  const getTitleForContentType = (contentType: string): string => {
    const titleMap: Record<string, string> = {
      'physiotherapy_evolution': 'Evolução Fisioterápica',
      'anamnesis': 'Anamnese',
      'results': 'Resultados Laboratoriais',
      'vitals': 'Sinais Vitais',
      'nutritional_assessment': 'Avaliação Nutricional',
      'meal_plan': 'Plano Alimentar',
      'training_plan': 'Plano de Treino',
      'body_composition': 'Composição Corporal',
      'psychotherapy_session': 'Sessão de Psicoterapia',
      'chiropractic_adjustment': 'Ajuste Quiroprático',
      'aesthetic_protocol': 'Protocolo Estético',
      'anthropometry': 'Avaliação Antropométrica',
      'exam': 'Exame',
      'report': 'Relatório',
      'prescription': 'Prescrição',
    };
    return titleMap[contentType] || 'Registro Clínico';
  };

  // ✅ CORREÇÃO 1 & 2: Fallback inteligente quando structured_records vazio OU incompleto
  const hasExtractedText = extractedText && extractedText.trim().length > 0;
  const isAnamnesis = contentType === 'anamnesis';

  // Detectar se há palavras-chave clínicas no texto
  const hasClinicalKeywords = hasExtractedText && /relata|dor|diagnóstico|queixa|histórico|sintoma|apresenta|hérnia|disco|patologia/i.test(extractedText);

  // Verificar se há clinical_note nos registros estruturados
  const hasClinicalNotes = data.records.some((r: any) => r.type === 'clinical_note');

  // Ativar fallback se:
  // 1. Não há registros OU
  // 2. É anamnese com palavras clínicas mas sem clinical_note
  const shouldUseFallback = (
    data.records.length === 0 ||
    (isAnamnesis && hasClinicalKeywords && !hasClinicalNotes)
  );

  if (shouldUseFallback && hasExtractedText) {
    console.warn(`[structureSmartfileRecords][${sessionId}] ⚠️ Fallback ativado:`, {
      recordsCount: data.records.length,
      isAnamnesis,
      hasClinicalKeywords,
      hasClinicalNotes,
      reason: data.records.length === 0 ? 'empty_records' : 'missing_clinical_notes'
    });

    // Buscar patient_id da sessão
    const { data: session } = await supabase
      .from("smartfile_sessions")
      .select("patient_id, ai_summary")
      .eq("id", sessionId)
      .single();

    // ✅ REMOVIDO: patient_health_records não é mais usado
    // Código removido - não criamos mais registros em patient_health_records
  }

  // Atualizar sessão
  await supabase
    .from("smartfile_sessions")
    .update({
      structured_records: data.records,
      status: "structured",
      step: 2,
    })
    .eq("id", sessionId);

  return data.records;
}

// 6. Gerar insights (Edge Function)
export async function generateSmartfileInsights(
  sessionId: string,
  records: any[],
  patientData?: any
): Promise<{ summary: string; insights: any }> {
  const { data, error } = await supabase.functions.invoke(
    "smartfile-v15-generate-insights-fast",
    {
      body: { records, patientData, sessionId },
    }
  );
  
  if (data?.aiModel) {
    console.log(`[generateSmartfileInsights] 🤖 Insights AI Model: ${data.aiModel.provider}/${data.aiModel.model_code} (${data.aiModel.name || 'N/A'})`);
  }

  if (error) {
    if (error.message?.includes("API_KEY") || error.message?.includes("GOOGLE_GEMINI_API_KEY")) {
      throw new Error("GOOGLE_GEMINI_API_KEY não configurada. Configure nas variáveis de ambiente.");
    }
    if (error.message?.includes("RESOURCE_EXHAUSTED") || error.message?.includes("429")) {
      throw new Error("Limite de requisições da API Google Gemini atingido. Tente novamente em alguns minutos.");
    }
    throw error;
  }
  if (!data.success) {
    throw new Error("Falha na geração de insights");
  }

  // Atualizar sessão
  await supabase
    .from("smartfile_sessions")
    .update({
      ai_summary: data.summary,
      ai_insights: data.insights,
      status: "insights_generated",
    })
    .eq("id", sessionId);

  return { summary: data.summary, insights: data.insights };
}

// 7. Gerar resultados (Edge Function)
export async function generateSmartfileResults(
  sessionId: string,
  patientId: string,
  records: any[]
): Promise<number | { created: number; metricsCreated: number }> {
  const timestamp = new Date().toISOString();
  console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] ===== START =====`);

  try {
    // STEP 1: Validar records
    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] STEP 1: Validating records`);
    if (!records || records.length === 0) {
      throw new Error("Nenhum registro encontrado para processar");
    }
    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] ✅ Records validated: ${records.length} records`);

    // STEP 2: Buscar sessão
    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] STEP 2: Fetching session`);
    const { data: session, error: sessionError } = await supabase
      .from('smartfile_sessions')
      .select('content_type')
      .eq('id', sessionId)
      .single();

    if (sessionError) {
      console.error(`[${timestamp}][generateSmartfileResults][${sessionId}] ❌ Session error:`, sessionError);
      throw new Error(`Erro ao buscar sessão: ${sessionError.message}`);
    }

    if (!session) {
      throw new Error("Sessão não encontrada");
    }

    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] ✅ Session found, content_type: ${session.content_type}`);

    // STEP 3: Obter autenticação
    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] STEP 3: Getting auth session`);
    const { data: { session: authSession }, error: authError } = await supabase.auth.getSession();

    if (authError) {
      console.error(`[${timestamp}][generateSmartfileResults][${sessionId}] ❌ Auth error:`, authError);
      throw new Error(`Erro de autenticação: ${authError.message}`);
    }

    if (!authSession?.user?.id) {
      console.error(`[${timestamp}][generateSmartfileResults][${sessionId}] ❌ No auth session or user ID`);
      throw new Error("Usuário não autenticado. Faça login novamente.");
    }

    const userId = authSession.user.id;
    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] ✅ Auth OK, userId: ${userId}`);

    // STEP 4: Invocar edge function
    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] STEP 4: Invoking edge function`);
    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] Payload:`, {
      sessionId,
      patientId,
      recordsCount: records.length,
      contentType: session.content_type,
      professionalId: userId
    });

    const { data, error } = await supabase.functions.invoke(
      "smartfile-v15-generate-results",
      {
        body: {
          sessionId,
          patientId,
          records,
          contentType: session.content_type || 'other',
          professionalId: userId
        },
        headers: {
          Authorization: `Bearer ${authSession.access_token}`
        }
      }
    );

    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] Edge function response:`, { data, error });

    if (error) {
      console.error(`[${timestamp}][generateSmartfileResults][${sessionId}] ❌ Edge function error:`, error);
      throw new Error(`Erro na função de geração: ${error.message || JSON.stringify(error)}`);
    }

    if (!data) {
      console.error(`[${timestamp}][generateSmartfileResults][${sessionId}] ❌ No data returned`);
      throw new Error("Nenhum dado retornado da função de geração");
    }

    if (!data.success) {
      console.error(`[${timestamp}][generateSmartfileResults][${sessionId}] ❌ Operation failed:`, data.error);
      throw new Error(data.error || "Falha na geração de resultados");
    }

    console.log(`[${timestamp}][generateSmartfileResults][${sessionId}] ✅ SUCCESS - Created: ${data.created}, Metrics: ${data.metricsCreated}`);

    if (data.metricsCreated !== undefined) {
      return {
        created: data.created || 0,
        metricsCreated: data.metricsCreated || 0
      };
    }

    return data.created || 0;

  } catch (error: any) {
    console.error(`[${timestamp}][generateSmartfileResults][${sessionId}] ❌❌❌ CRITICAL ERROR:`, {
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      fullError: error
    });
    throw error;
  }
}

// 8. Obter sessão
export async function getSmartfileSession(
  sessionId: string
): Promise<SmartfileSession> {
  const { data, error } = await supabase
    .from("smartfile_sessions")
    .select("*")
    .eq("id", sessionId)
    .single();

  if (error) throw error;
  return data as SmartfileSession;
}

// 9. Atualizar notas profissionais
export async function updateSmartfileProfessionalNotes(
  sessionId: string,
  notes: string
): Promise<void> {
  const { error } = await supabase
    .from("smartfile_sessions")
    .update({ professional_notes: notes })
    .eq("id", sessionId);

  if (error) throw error;
}

// 10. Finalizar sessão
export async function finalizeSmartfileSession(
  sessionId: string
): Promise<void> {
  const { error } = await supabase
    .from("smartfile_sessions")
    .update({
      status: "completed",
      completed_at: new Date().toISOString(),
    })
    .eq("id", sessionId);

  if (error) throw error;
}

// 11. Extrair informações do paciente do texto
export async function extractPatientInfoFromText(
  extractedText: string
): Promise<{ name: string | null; cpf: string | null; confidence: number }> {
  const { data, error } = await supabase.functions.invoke(
    "smartfile-extract-patient-info",
    {
      body: { extractedText },
    }
  );

  if (error || !data.success) {
    console.warn("Falha ao extrair dados do paciente:", error);
    return { name: null, cpf: null, confidence: 0 };
  }

  return {
    name: data.patientName,
    cpf: data.patientCPF,
    confidence: data.confidence || 0,
  };
}

// 12. Buscar pacientes por nome ou CPF
export async function searchPatientsByNameOrCPF(
  query: string
): Promise<Array<{ id: string; full_name: string; cpf: string }>> {
  if (!query || query.trim().length < 3) return [];

  const { data, error } = await supabase
    .from("patients_onboarding")
    .select("id, full_name, cpf")
    .or(`full_name.ilike.%${query}%,cpf.ilike.%${query}%`)
    .limit(10);

  if (error) {
    console.error("Erro ao buscar pacientes:", error);
    return [];
  }

  return data || [];
}

/**
 * Busca fuzzy de pacientes com scoring por similaridade
 */
export async function fuzzySearchPatients(
  query: string,
  cpf?: string | null,
  threshold = 0.3
): Promise<Array<{
  id: string;
  full_name: string;
  cpf: string;
  similarity: number;
  matchType: string;
}>> {
  const { data, error } = await supabase.functions.invoke('fuzzy-search-patients', {
    body: { query, cpf, threshold }
  });

  if (error) {
    console.error("Erro na busca fuzzy:", error);
    return [];
  }

  return data?.results || [];
}

// 14. Vincular paciente à sessão
export async function linkPatientToSmartfileSession(
  sessionId: string,
  patientId: string
): Promise<void> {
  // Atualizar sessão
  const { error } = await supabase
    .from("smartfile_sessions")
    .update({
      patient_id: patientId,
      temp_patient_data: null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", sessionId);

  if (error) throw error;
}

// FLUXO COMPLETO: Upload de arquivo tradicional (LEGACY - mantido para compatibilidade)
export async function processFileUploadLegacy(
  sessionId: string,
  file: File,
  onProgress?: (stage: string, progress: number) => void
): Promise<void> {
  try {
    // 1. Upload
    onProgress?.("Enviando arquivo...", 20);
    const { filePath, fileType } = await uploadSmartfileFile(sessionId, file);

    // 2. Extração
    onProgress?.("Extraindo conteúdo...", 40);
    const extractedText = await extractSmartfileText(sessionId, filePath, fileType, file);

    // 3. Classificação
    onProgress?.("Classificando documento...", 60);
    const contentType = await classifySmartfileContent(sessionId, extractedText, fileType);

    // 4. Estruturação
    onProgress?.("Estruturando dados...", 80);
    await structureSmartfileRecords(sessionId, extractedText, contentType);

    onProgress?.("Concluído!", 100);
  } catch (error: any) {
    // Marcar sessão como falha
    await supabase
      .from("smartfile_sessions")
      .update({ status: "failed" })
      .eq("id", sessionId);
    throw error;
  }
}

// Fase 1.3: NOVO FLUXO ASSÍNCRONO - Upload rápido + processamento assíncrono otimizado
export async function processFileUploadFast(
  sessionId: string,
  file: File,
  onProgress?: (stage: string, progress: number) => void
): Promise<void> {
  const tracker = new PerformanceTracker();
  tracker.start();
  tracker.mark('upload_start');

  try {
    // FASE 1: Upload + Extração local (bloqueante - usuário espera)
    onProgress?.("Enviando arquivo...", 10);
    tracker.mark('upload_start');
    const { filePath, fileType } = await uploadSmartfileFile(sessionId, file);
    tracker.mark('upload_end');

    // FASE 2: Extração local (se suportado) ou upload para preview
    onProgress?.("Extraindo conteúdo...", 30);
    tracker.mark('extraction_start');
    const extractedText = await extractSmartfileText(sessionId, filePath, fileType, file);
    tracker.mark('extraction_end');

    // FASE 3: Salvar preview no DB e permitir usuário usar
    onProgress?.("Preparando preview...", 50);
    tracker.mark('preview_save_start');

    // Salvar preview básico (extracted_text já foi salvo)
    await supabase
      .from("smartfile_sessions")
      .update({
        status: "extracted",
        step: 2, // Permitir usuário avançar para Step2
      })
      .eq("id", sessionId);

    tracker.mark('preview_save_end');

    // FASE 4: Processamento assíncrono (aguardado)
    onProgress?.("Classificando e estruturando...", 60);
    tracker.mark('processing_start');

    try {
      await processStructuredData(sessionId, extractedText, fileType, tracker);
      onProgress?.("Pronto para usar!", 100);
    } catch (error: any) {
      console.error('[processFileUploadFast] Erro no processamento:', error);
      // Marcar como falha
      await supabase
        .from("smartfile_sessions")
        .update({ status: "failed", error_message: error.message })
        .eq("id", sessionId);
      throw error;
    }

  } catch (error: any) {
    tracker.mark('error');
    // Marcar sessão como falha
    await supabase
      .from("smartfile_sessions")
      .update({ status: "failed" })
      .eq("id", sessionId);
    throw error;
  }
}

// Fase 2.3: Processamento assíncrono com paralelização otimizada
async function processStructuredData(
  sessionId: string,
  extractedText: string,
  fileType: string,
  tracker: PerformanceTracker
): Promise<void> {
  try {
    tracker.mark('classification_start');

    // Otimização: Paralelização - classificar e extrair metadata em paralelo
    const [contentType, metadata] = await Promise.all([
      classifySmartfileContent(sessionId, extractedText, fileType),
      extractMetadata(extractedText, fileType),
    ]);

    tracker.mark('classification_end');
    tracker.mark('structure_start');

    // Estruturação (depende do contentType)
    await structureSmartfileRecords(sessionId, extractedText, contentType);

    tracker.mark('structure_end');

    // Otimização: Executar salvamento de métricas e atualização de status em paralelo
    const metrics = tracker.end({
      extractionMethod: 'client',
      contentType,
    });

    await Promise.all([
      PerformanceTracker.saveMetrics(sessionId, metrics),
      supabase
        .from("smartfile_sessions")
        .update({
          status: "structured",
          metadata: metadata,
        })
        .eq("id", sessionId)
    ]);

    console.log(`[processStructuredData] ✅ Processamento concluído para sessão ${sessionId}`);
  } catch (error) {
    console.error(`[processStructuredData] ❌ Erro:`, error);
    throw error;
  }
}

// Helper: Extrair metadata básica do texto (processa em paralelo com classificação)
async function extractMetadata(text: string, fileType: string): Promise<Record<string, any>> {
  // Extração simples de metadata (pode ser expandida)
  return {
    textLength: text.length,
    fileType,
    wordCount: text.split(/\s+/).length,
    extractedAt: new Date().toISOString(),
  };
}

// ⚡ NOVA VERSÃO ULTRA-RÁPIDA (3-5x mais rápido)
// Importação movida para o topo do arquivo

// Exportar versões
export { processFileUploadUltraFast, cleanOldCache };

// Manter compatibilidade: usar versão ULTRA-RÁPIDA por padrão
export const processFileUpload = processFileUploadUltraFast;
